import React, { useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import {
  CheckCircle,
  MenuBook,
  LiveHelp,
  Chat,
} from '@material-ui/icons';
import clsx from 'clsx';
import { formatLastSyncTimeLocalized } from '../../features/catalog-sync/catalogSyncUtils';
import { LiveStatus, PublishState } from '../../features/home/operatorState';
import { useOperatorState } from '../../features/home/hooks/useOperatorState';
import { useCatalogSync } from '../../features/home/hooks/useCatalogSync';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { usePreviewOpener } from '../../features/shell/preview/usePreviewOpener';
import { LIVE_PUBLISH_SUCCESS_EVENT } from '../../features/home/livePublishEvents';
import { deriveChecklistSteps } from '../../features/home/deriveChecklistSteps';
import { shouldShowOperatorDashboard } from '../../features/home/homeMode';
import AgentMcpSetupCard from '../../features/home/components/AgentMcpSetupCard';
import styles from './HomeSidebar.module.scss';
import TawkHelper from '../../util/tawkHelper';

function capitalize(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const HomeSidebar = () => {
  const localizeMessage = useLocalizedMessage();
  const { operatorState, isLoading } = useOperatorState();
  const catalogSync = useCatalogSync();
  const { previewShopAddress, openStorefrontPreview } = usePreviewOpener();
  const [publishSuccessObserved, setPublishSuccessObserved] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePublishSuccess = () => setPublishSuccessObserved(true);

    window.addEventListener(LIVE_PUBLISH_SUCCESS_EVENT, handlePublishSuccess);
    return () => {
      window.removeEventListener(LIVE_PUBLISH_SUCCESS_EVENT, handlePublishSuccess);
    };
  }, []);

  if (isLoading) {
    return (
      <aside
        className={styles.sidebar}
        data-testid="home-sidebar-loading"
        role="status"
        aria-busy="true"
        aria-label={localizeMessage('operator.home.loading')}
      >
        <div className={styles.statusSkeleton} aria-hidden="true" />
        <div className={styles.helpSkeleton} aria-hidden="true" />
      </aside>
    );
  }

  const live = operatorState?.live;
  const publishState = operatorState?.publishState;
  const hasPublishedBundle = publishState === PublishState.PUBLISHED
    || publishState === PublishState.UNPUBLISHED_CHANGES;
  const isLiveOn = publishSuccessObserved
    || live === LiveStatus.ACTIVE
    || (
      hasPublishedBundle
      && live !== LiveStatus.NOT_ACTIVE
      && live !== LiveStatus.ERROR
    );
  const isLiveOff = !isLiveOn && live === LiveStatus.NOT_ACTIVE;
  const liveStatusState = isLiveOn ? 'on' : isLiveOff ? 'off' : 'unknown';
  const lastSuccessAt = (catalogSync && !catalogSync.isDegraded && catalogSync.lastSuccessAt) || null;
  const showOperatingStatus = shouldShowOperatorDashboard(
    operatorState,
    deriveChecklistSteps(operatorState),
  );

  const handleOpenPreview = () => {
    if (previewShopAddress) openStorefrontPreview();
  };

  const handleOpenDirectMessage = () => {
    TawkHelper.openSupportChat();
  };

  return (
    <aside className={styles.sidebar}>
      {showOperatingStatus && (
        <>
          <div className={styles.sidebarCard} data-testid="sidebar-status-card">
            <div className={styles.sidebarRow}>
              <div className={styles.sidebarStatusGroup}>
                <span className={styles.sidebarStatusLabel}>
                  {localizeMessage('operator.home.status.title')}
                </span>
                <div
                  className={clsx(
                    styles.sidebarStatus,
                    isLiveOn && styles.statusOn,
                    isLiveOff && styles.statusOff,
                    !isLiveOn && !isLiveOff && styles.statusUnknown,
                  )}
                  data-live-status={liveStatusState}
                  data-testid="sidebar-live-status"
                >
                  {capitalize(
                    isLiveOn
                      ? localizeMessage('operator.home.status.on')
                      : isLiveOff
                        ? localizeMessage('operator.home.status.off')
                        : localizeMessage('operator.home.live.unknown'),
                  )}
                </div>
              </div>
              {!isLiveOn && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenPreview}
                  disabled={!previewShopAddress}
                  data-testid="sidebar-open-preview-btn"
                >
                  {localizeMessage('operator.home.status.open_preview')}
                </Button>
              )}
            </div>
            <div className={styles.sidebarCheck}>
              <CheckCircle className={styles.sidebarCheckIcon} style={{ fontSize: 16 }} />
              <span>
                {localizeMessage('operator.home.indexing.last_catalog_update')}{' '}
                {formatLastSyncTimeLocalized(lastSuccessAt, localizeMessage)}
              </span>
            </div>
          </div>

          <AgentMcpSetupCard variant="status" />
        </>
      )}

      <div className={styles.sidebarCard} data-testid="sidebar-help-card">
        <p className={styles.helpTitle}>
          {localizeMessage('operator.home.help.title')}
        </p>
        <p className={styles.helpHint}>
          {localizeMessage('operator.home.help.hint')}
        </p>
        <div className={styles.helpActions}>
          <Button
            size="small"
            variant="outlined"
            className={styles.helpBtn}
            startIcon={<MenuBook style={{ fontSize: 16 }} />}
            component="a"
            href="https://docs.kealabs.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="help-documentation-btn"
          >
            {localizeMessage('operator.home.help.documentation')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            className={styles.helpBtn}
            startIcon={<LiveHelp style={{ fontSize: 16 }} />}
            component="a"
            href="http://kealabs.com/pro/faq"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="help-faq-btn"
          >
            {localizeMessage('operator.home.help.faq')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            className={styles.helpBtn}
            startIcon={<Chat style={{ fontSize: 16 }} />}
            onClick={handleOpenDirectMessage}
            data-testid="help-direct-message-btn"
          >
            {localizeMessage('operator.home.help.direct_message')}
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default HomeSidebar;
